import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { cva, type VariantProps } from 'class-variance-authority';

import {
  getEnrichmentStatusConfig,
  getPoliceReportStatusConfig,
  type PoliceDepartmentEnrichmentStatus,
  type PoliceReportAttestationStatus,
} from '@/components/police/utils/policeUtils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const statusBadgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium border',
  {
    variants: {
      variant: {
        default: 'bg-blue-50 text-blue-700 border-blue-200',
        secondary: 'bg-gray-100 text-gray-700 border-gray-200',
        destructive: 'bg-red-50 text-red-700 border-red-200',
        outline: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      },
      size: {
        default: 'px-2.5 py-0.5 text-xs',
        sm: 'px-2 py-0.5 text-xs',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const iconMap = {
  'question-mark-circle': InformationCircleIcon,
  'exclamation-triangle': ExclamationTriangleIcon,
  'x-circle': ExclamationTriangleIcon,
  'check-circle': CheckCircleIcon,
  'document-check': CheckCircleIcon,
  'information-circle': InformationCircleIcon,
  'arrow-path': ArrowPathIcon,
};

export interface PoliceStatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {
  /** The attestation status to display */
  status?: PoliceReportAttestationStatus | null;
  /** The enrichment status to display (alternative to attestation status) */
  enrichmentStatus?: PoliceDepartmentEnrichmentStatus | null;
  /** Whether to show the full label or short label */
  showFullLabel?: boolean;
  /** Whether to show an icon */
  showIcon?: boolean;
  /** Whether to show a tooltip with description */
  showTooltip?: boolean;
  /** Whether to animate if status is in progress */
  animate?: boolean;
}

export const PoliceStatusBadge = ({
  status,
  enrichmentStatus,
  showFullLabel = false,
  showIcon = true,
  showTooltip = false,
  animate = true,
  size,
  className,
  ...props
}: PoliceStatusBadgeProps) => {
  // Handle enrichment status
  if (enrichmentStatus) {
    const enrichConfig = getEnrichmentStatusConfig(enrichmentStatus);
    const label = showFullLabel ? enrichConfig.label : enrichConfig.shortLabel;
    const IconComponent =
      showIcon && enrichConfig.icon
        ? iconMap[enrichConfig.icon as keyof typeof iconMap]
        : null;
    const shouldAnimate =
      animate && enrichmentStatus === 'enrichment_in_progress';
    const badgeColorClass = `${enrichConfig.badgeColor.bg} ${enrichConfig.badgeColor.text} ${enrichConfig.badgeColor.border || ''}`;

    const badge = (
      <span
        className={cn(
          statusBadgeVariants({ variant: enrichConfig.variant, size }),
          badgeColorClass,
          className,
        )}
        {...props}
      >
        {IconComponent && (
          <IconComponent
            className={cn('h-3 w-3', shouldAnimate && 'animate-spin')}
          />
        )}
        {label}
      </span>
    );

    if (showTooltip) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent>
            <div className="space-y-1">
              <div className="font-medium">{enrichConfig.label}</div>
              <div className="text-xs">{enrichConfig.description}</div>
            </div>
          </TooltipContent>
        </Tooltip>
      );
    }

    return badge;
  }

  // Handle police report status
  const reportConfig = getPoliceReportStatusConfig(status);
  const label = showFullLabel ? reportConfig.label : reportConfig.shortLabel;
  const IconComponent =
    showIcon && reportConfig.icon
      ? iconMap[reportConfig.icon as keyof typeof iconMap]
      : null;
  const badgeColorClass = `${reportConfig.badgeColor.bg} ${reportConfig.badgeColor.text} ${reportConfig.badgeColor.border || ''}`;

  const badge = (
    <span
      className={cn(
        statusBadgeVariants({ variant: reportConfig.variant, size }),
        badgeColorClass,
        className,
      )}
      {...props}
    >
      {IconComponent && <IconComponent className="h-3 w-3" />}
      {label}
    </span>
  );

  if (showTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <div className="font-medium">{reportConfig.label}</div>
            <div className="text-xs">{reportConfig.description}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  return badge;
};
